import type {ImageField} from '@prismicio/client'
import {isFilled} from '@prismicio/client'
import {PrismicNextImage} from '@prismicio/next/pages'
import cn from 'classnames'
import type {Transition} from 'framer-motion'
import {AnimatePresence, motion, useReducedMotion} from 'framer-motion'
import {useId, useState} from 'react'
import {Icon} from 'src/common/Icon'
import {DEFAULT_PADDING, PADDING_BOTTOM_CLASS, PADDING_TOP_CLASS} from 'src/common/paddingClasses'
import {AnimatedBlobBackground} from './AnimatedBlobBackground'

export type MultiStepBlobSectionStep = {
  title: string
  description: string
  image: ImageField
}

export type MultiStepBlobSectionBackgroundMode = 'Blobs' | 'Solid green'

const descriptionWidthMap: Record<string, string> = {
  'Small (432px)': 'max-w-[432px]',
  'Medium (560px)': 'max-w-[560px]',
  'Large (680px)': 'max-w-[680px]',
}

export type MultiStepBlobSectionUIProps = {
  eyebrow: string
  title: string
  description: string
  descriptionWidth?: string | null
  steps: MultiStepBlobSectionStep[]
  backgroundMode: MultiStepBlobSectionBackgroundMode
  paddingTop?: string | null
  paddingBottom?: string | null
}

export const MultiStepBlobSection = ({
  eyebrow,
  title,
  description,
  descriptionWidth,
  steps,
  backgroundMode,
  paddingTop,
  paddingBottom,
}: MultiStepBlobSectionUIProps) => {
  const [activeStep, setActiveStep] = useState(0)
  const panelIdPrefix = useId()
  const shouldReduceMotion = useReducedMotion()
  const expandTransition: Transition = shouldReduceMotion ? {duration: 0} : {duration: 0.3, ease: 'easeInOut'}
  const activeImage = steps[activeStep]?.image
  const paddingTopClass = PADDING_TOP_CLASS[paddingTop ?? DEFAULT_PADDING] ?? ''
  const paddingBottomClass = PADDING_BOTTOM_CLASS[paddingBottom ?? DEFAULT_PADDING] ?? ''

  return (
    <section
      data-slice-type="blob_section"
      data-slice-variation="multiStep"
      data-nav-dark="true"
      className={cn(
        'relative overflow-hidden bg-green-dark px-5 md:px-10 lg:px-5 xl:px-0',
        paddingTopClass,
        paddingBottomClass,
        'max-md:!pb-24',
      )}
    >
      {backgroundMode === 'Blobs' && <AnimatedBlobBackground />}
      <div className="relative z-10 flex flex-col items-center gap-12">
        <div className="flex flex-col items-center text-center">
          {eyebrow && (
            <span className="mb-6 rounded-full border border-black/10 bg-moss-white px-4 py-2 text-13 font-semibold uppercase tracking-[0.78px] text-moss-grey-500">
              {eyebrow}
            </span>
          )}
          <h2 className="font-main text-40 leading-[42px] tracking-[-0.4px] text-moss-white md:leading-tight lg:text-64 lg:leading-[64px] lg:tracking-[-0.64px]">
            {title}
          </h2>
          {description && (
            <p
              className={cn(
                'mt-2 font-main text-18 leading-6 text-moss-white md:mt-4',
                descriptionWidthMap[descriptionWidth ?? 'Small (432px)'] ?? 'max-w-[432px]',
              )}
            >
              {description}
            </p>
          )}
        </div>
        {steps.length > 0 && (
          <div className="mx-auto flex w-full max-w-[1200px] flex-col items-center lg:h-[390px] lg:flex-row lg:justify-between lg:gap-10">
            <ol className="flex w-full max-w-[640px] flex-col lg:w-[490px] lg:max-w-none">
              {steps.map((step, index) => {
                const isActive = index === activeStep
                const panelId = `${panelIdPrefix}-step-${index}`

                return (
                  <li
                    key={`${index}-${step.title || 'step'}`}
                    className="border-b border-moss-white/10 last:border-b-0"
                  >
                    <button
                      type="button"
                      onClick={() => setActiveStep(index)}
                      aria-expanded={isActive}
                      aria-controls={isActive ? panelId : undefined}
                      className="flex w-full items-center gap-5 py-4 text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-audit-lime"
                    >
                      <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-ui-blue-450 text-12 font-semibold text-moss-white">
                        {index + 1}
                      </span>
                      <span
                        className={cn(
                          'flex-1 font-main text-24 text-moss-white transition-opacity duration-300',
                          !isActive && 'opacity-60',
                        )}
                      >
                        {step.title}
                      </span>
                      <span aria-hidden="true" className="lg:hidden">
                        <Icon
                          name="chevron-v2"
                          className={cn(
                            'h-2 w-3 transition-transform duration-300',
                            isActive ? 'text-audit-lime' : 'rotate-180 text-moss-white/60',
                          )}
                        />
                      </span>
                    </button>
                    <AnimatePresence initial={false}>
                      {isActive && (
                        <motion.div
                          id={panelId}
                          initial={{opacity: 0, height: 0}}
                          animate={{opacity: 1, height: 'auto'}}
                          exit={{opacity: 0, height: 0}}
                          transition={expandTransition}
                          className="overflow-hidden"
                        >
                          {step.description && (
                            <p className="pb-4 pl-11 text-18 leading-6 text-moss-white">{step.description}</p>
                          )}
                          {isFilled.image(step.image) && (
                            <div className="mb-4 overflow-hidden rounded-lg bg-[#345142] lg:hidden">
                              <PrismicNextImage field={step.image} className="h-auto w-full" />
                            </div>
                          )}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </li>
                )
              })}
            </ol>
            <div className="relative hidden items-center justify-center overflow-hidden rounded-lg bg-[#345142] lg:flex lg:h-full lg:w-[583px]">
              <AnimatePresence mode="wait">
                {activeImage && isFilled.image(activeImage) && (
                  <motion.div
                    key={activeStep}
                    initial={{opacity: 0}}
                    animate={{opacity: 1}}
                    exit={{opacity: 0}}
                    transition={{duration: 0.2, ease: 'easeInOut'}}
                    className="flex size-full items-center justify-center"
                  >
                    <PrismicNextImage field={activeImage} className="max-h-full max-w-full object-contain" />
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        )}
      </div>
    </section>
  )
}
